import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsPage = () => {
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (res.ok) {
          const data = await res.json();
          setProducts(data);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Get product name based on current language
  const getProductName = (product) => {
    if (currentLang === 'ar' || currentLang === 'ku') return product.name_ar;
    if (currentLang === 'de') return product.name_de || product.name_en;
    return product.name_en;
  };

  // Get product description based on current language
  const getProductDesc = (product) => {
    if (currentLang === 'ar' || currentLang === 'ku') return product.description_ar;
    if (currentLang === 'de') return product.description_de || product.description_en;
    return product.description_en;
  };

  // Get secondary name (opposite language)
  const getSecondaryName = (product) => {
    if (currentLang === 'ar' || currentLang === 'ku') return product.name_en;
    return product.name_ar;
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] pt-24">
      {/* Page Header */}
      <div className="bg-[#0d4023] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-2xl ${isRTL ? 'ms-auto text-end' : 'me-auto text-start'}`}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
              {currentLang === 'ar' ? 'تشكيلتنا' : currentLang === 'de' ? 'Unsere Auswahl' : 'Our Selection'}
            </span>
            <h1 
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight"
              data-testid="products-title"
            >
              {t.products?.title || 'منتجاتنا'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              {t.products?.subtitle || 'اكتشف تشكيلتنا الواسعة من الخبز العربي والمعجنات الشرقية الأصيلة'}
            </p>
          </div>
        </div>
      </div>

      {/* Products Grid - Asymmetric Masonry Style */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {loading ? (
          <div className="text-center py-16">
            <p className="text-[#5a7062]">
              {currentLang === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.filter(p => p.is_available).map((product, index) => {
            // Make some cards larger for visual interest
            const isLarge = index === 0 || index === 3;
            
            return (
              <div 
                key={product.id}
                className={`group ${isLarge ? 'lg:col-span-2 lg:row-span-1' : ''}`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="relative overflow-hidden rounded-2xl border border-[#b89759]/10 hover:border-[#b89759]/30 transition-colors duration-300 bg-white">
                  {/* Image Container */}
                  <div className={`relative overflow-hidden ${isLarge ? 'aspect-[16/9]' : 'aspect-[4/5]'}`}>
                    <img 
                      src={product.image_url || 'https://via.placeholder.com/400x500'}
                      alt={getProductName(product)}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d4023]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Badge */}
                    <div 
                      className={`absolute top-4 ${isRTL ? 'start-4' : 'end-4'} px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider`}
                      style={{ 
                        backgroundColor: '#b89759',
                        color: '#0d4023'
                      }}
                    >
                      €{product.price.toFixed(2)}
                    </div>

                    {/* Hover Content */}
                    <div className={`absolute bottom-0 start-0 end-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ${isRTL ? 'text-end' : 'text-start'}`}>
                      <p className="text-white/90 text-sm leading-relaxed">
                        {getProductDesc(product)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div className={`p-6 ${isRTL ? 'text-end' : 'text-start'}`}>
                    <h3 className="font-heading text-xl lg:text-2xl font-bold text-[#0d4023] mb-1">
                      {getProductName(product)}
                    </h3>
                    <p className="text-[#5a7062] text-sm">
                      {getSecondaryName(product)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-[#0d4023]/5 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0d4023] mb-4">
            {currentLang === 'ar' ? 'تبحث عن طلب بالجملة؟' : currentLang === 'de' ? 'Großbestellung gesucht?' : 'Looking for Bulk Orders?'}
          </h2>
          <p className="text-[#5a7062] mb-8 max-w-xl mx-auto">
            {currentLang === 'ar' 
              ? 'نقدم أسعار خاصة للمطاعم والسوبرماركت والموزعين'
              : currentLang === 'de'
              ? 'Wir bieten Sonderpreise für Restaurants, Supermärkte und Händler'
              : 'We offer special prices for restaurants, supermarkets and distributors'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#0d4023] text-[#faf8f5] rounded-full font-medium hover:bg-[#1a5c32] transition-colors duration-300"
            data-testid="bulk-order-cta"
          >
            {currentLang === 'ar' ? 'تواصل معنا' : currentLang === 'de' ? 'Kontaktieren Sie uns' : 'Contact Us'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
